/*
 * /prescription/$patientId (page)
 */
import {
	Badge,
	Button,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	Container,
	Page,
	Placeholder,
} from "@emr/react-ui"
import { useAuth } from "@emr/auth/client"
import { createFileRoute } from "@tanstack/react-router"
import { useState } from "react"
import { Plus, X } from "lucide-react"
import { PrescriptionForm } from "../../features/prescription/cannabis/prescription-form"
import { HistoricPrescriptions } from "../../features/prescription/cannabis/historic-prescriptions"
import { PatientHeader } from "../../features/patient/patient-header"
import { trpc } from "../../trpc"

export const Route = createFileRoute("/prescription/$patientId")({
	component: PrescriptionPage,
})

const MOCK_PATIENT = {
	firstName: "...",
	lastName: "",
	dateOfBirth: null,
	bloodType: null,
	allergies: null,
	emergencyContactName: null,
	emergencyContactPhone: null,
}

function PrescriptionPage() {
	const { patientId } = Route.useParams()
	const { role } = useAuth()
	const [isDirty, setIsDirty] = useState(false)
	const [isCreatingNew, setIsCreatingNew] = useState(false)

	const isAdmin = role === "admin" || role === "super_admin"

	const { data: patient, isLoading: patientLoading } =
		trpc.patient.getByFilter.useQuery({ where: { id: patientId } })

	// Practitioners see only their own record; admins see all
	const { data: myPractitioner, isLoading: myPracLoading } =
		trpc.practitioner.getByFilter.useQuery(
			{ where: {} },
			{ enabled: !isAdmin },
		)

	const { data: allPractitioners, isLoading: allPracLoading } =
		trpc.practitioner.getAllByFilter.useQuery(
			{ where: { active: true } },
			{ enabled: isAdmin },
		)

	const { data: prescription, isLoading: prescriptionLoading } =
		trpc.prescription.getByFilter.useQuery({
			where: { patientId, isLatest: true, active: true },
		})

	const { data: existingLpPrescriptions, isLoading: lpLoading } =
		trpc.lpPrescription.getAllByFilter.useQuery(
			{ where: { prescriptionId: prescription?.id, active: true } },
			{ enabled: !!prescription?.id },
		)

	const { data: patientDiagnoses, isLoading: patientDiagnosesLoading } =
		trpc.patientDiagnosis.getAllByFilter.useQuery({ where: { patientId, active: true } })

	const { data: allDiagnoses, isLoading: allDiagnosesLoading } =
		trpc.diagnosis.getAllByFilter.useQuery({ where: { active: true } })

	const fullDiagnosisText = (() => {
		if (!patientDiagnoses?.length || !allDiagnoses?.length) return ""
		const diagnosisMap = Object.fromEntries(allDiagnoses.map((d) => [d.id, d.name]))
		const primary = patientDiagnoses.find((pd) => pd.type === "primary")
		const secondaries = patientDiagnoses.filter((pd) => pd.type === "secondary")
		const parts = [
			...(primary ? [diagnosisMap[primary.diagnosisId] ?? ""] : []),
			...secondaries.map((pd) => diagnosisMap[pd.diagnosisId] ?? ""),
		].filter(Boolean)
		return parts.join(", ")
	})()

	const isLoading =
		patientLoading ||
		(isAdmin ? allPracLoading : myPracLoading) ||
		prescriptionLoading ||
		lpLoading ||
		patientDiagnosesLoading ||
		allDiagnosesLoading

	// For practitioner role: their own ID; for admin: null (must select in form)
	const defaultPractitionerId = isAdmin ? null : (myPractitioner?.id ?? null)

	// Admin: build options list; practitioner: no options (auto-set)
	const practitionerOptions = isAdmin
		? (allPractitioners ?? []).map((p) => ({
				label: `${p.firstName} ${p.lastName}${(p as any).professionalDesignation ? `, ${(p as any).professionalDesignation}` : ""}`,
				value: p.id,
			}))
		: undefined

	const displayPatient = patient ?? (MOCK_PATIENT as any)

	function handleNewSuccess() {
		setIsCreatingNew(false)
		setIsDirty(false)
	}

	return (
		<Page centerVertical={false}>
			<PatientHeader patient={displayPatient} patientId={patientId} isDirty={isDirty} />
			<Container width="xl" padding="lg" gap="lg">
				{isLoading ? (
					<Placeholder title="Loading patient data..." />
				) : prescription ? (
					<>
						{/* New prescription form — on top when creating */}
						{isCreatingNew && (
							<Card>
								<CardHeader>
									<div className="flex items-center justify-between">
										<div>
											<CardTitle>New Prescription</CardTitle>
											<CardDescription>
												Saving will archive the current prescription and activate this one.
											</CardDescription>
										</div>
										<Button
											size="sm"
											variant="ghost"
											onClick={() => {
												setIsCreatingNew(false)
												setIsDirty(false)
											}}
										>
											<X className="w-4 h-4 mr-1" />
											Cancel
										</Button>
									</div>
								</CardHeader>
								<CardContent>
									<PrescriptionForm
										patientId={patientId}
										practitionerId={defaultPractitionerId}
										practitionerOptions={practitionerOptions}
										autoFillDiagnosis={fullDiagnosisText}
										onDirtyChange={setIsDirty}
										onSuccess={handleNewSuccess}
									/>
								</CardContent>
							</Card>
						)}

						{/* Active prescription — read-only */}
						<Card>
							<CardHeader>
								<div className="flex items-center justify-between">
									<div>
										<CardTitle>Active Prescription</CardTitle>
										{prescription.startDate && (
											<CardDescription>
												Started{" "}
												{new Date(prescription.startDate).toLocaleDateString("en-CA", {
													year: "numeric",
													month: "short",
													day: "numeric",
												})}
												{prescription.endDate && (
													<>
														{" "}· Expires{" "}
														{new Date(prescription.endDate).toLocaleDateString("en-CA", {
															year: "numeric",
															month: "short",
															day: "numeric",
														})}
													</>
												)}
											</CardDescription>
										)}
									</div>
									<div className="flex items-center gap-2">
										<Badge variant="secondary">Active</Badge>
										{!isCreatingNew && (
											<Button
												size="sm"
												variant="outline"
												onClick={() => setIsCreatingNew(true)}
											>
												<Plus className="w-4 h-4 mr-1" />
												New Prescription
											</Button>
										)}
									</div>
								</div>
							</CardHeader>
							<CardContent>
								<PrescriptionForm
									patientId={patientId}
									practitionerId={prescription.practitionerId ?? defaultPractitionerId}
									prescription={prescription}
									existingLpPrescriptions={existingLpPrescriptions ?? undefined}
									disabled={true}
								/>
							</CardContent>
						</Card>
					</>
				) : (
					/* No existing prescription — show create form */
					<Card>
						<CardHeader>
							<CardTitle>New Prescription</CardTitle>
						</CardHeader>
						<CardContent>
							<PrescriptionForm
								patientId={patientId}
								practitionerId={defaultPractitionerId}
								practitionerOptions={practitionerOptions}
								autoFillDiagnosis={fullDiagnosisText}
								onDirtyChange={setIsDirty}
							/>
						</CardContent>
					</Card>
				)}
				{!isLoading && <HistoricPrescriptions patientId={patientId} />}
			</Container>
		</Page>
	)
}
